import type { SupabaseClient } from '../client'
import type { Database } from '../database.types'
import { SupabaseError } from '../errors'
import { unwrapMaybe } from './_helpers'

export type ProjectPreferences = Database['portal']['Tables']['project_preferences']['Row']
export type NotificationFrequency = 'immediate' | 'daily' | 'weekly' | 'none'

const COLUMNS =
  'id, project_id, client_user_id, notification_frequency, avatar_url, language, updated_at'

const DEFAULTS: Omit<ProjectPreferences, 'id' | 'project_id' | 'client_user_id' | 'updated_at'> = {
  notification_frequency: 'immediate',
  avatar_url: null,
  language: 'pt-BR',
}

/**
 * Busca preferences do cliente pra um projeto. Retorna defaults se nao existe.
 */
export async function getProjectPreferences(
  client: SupabaseClient,
  projectId: number,
  clientUserId: string,
): Promise<ProjectPreferences> {
  const result = await client
    .schema('portal')
    .from('project_preferences')
    .select(COLUMNS)
    .eq('project_id', projectId)
    .eq('client_user_id', clientUserId)
    .maybeSingle()

  const row = unwrapMaybe(result) as ProjectPreferences | null
  if (row) return row

  return {
    id: 0,
    project_id: projectId,
    client_user_id: clientUserId,
    notification_frequency: DEFAULTS.notification_frequency,
    avatar_url: DEFAULTS.avatar_url,
    language: DEFAULTS.language,
    updated_at: new Date().toISOString(),
  }
}

export type UpsertProjectPreferencesInput = {
  notification_frequency?: NotificationFrequency
  avatar_url?: string | null
  language?: string
}

/**
 * Cria ou atualiza preferences via upsert (constraint unique por
 * project_id+client_user_id).
 */
export async function upsertProjectPreferences(
  client: SupabaseClient,
  projectId: number,
  clientUserId: string,
  fields: UpsertProjectPreferencesInput,
): Promise<ProjectPreferences> {
  const payload = {
    project_id: projectId,
    client_user_id: clientUserId,
    ...fields,
    updated_at: new Date().toISOString(),
  }

  const result = await client
    .schema('portal')
    .from('project_preferences')
    .upsert(payload, { onConflict: 'project_id,client_user_id' })
    .select(COLUMNS)
    .single()

  if (result.error) throw new SupabaseError(result.error.message, result.error)
  return result.data as ProjectPreferences
}
